import { useEffect, useState } from 'react'
import * as React from 'react'
import { RouteComponentProps, useLocation } from 'react-router'
import queryString from 'query-string'
/* Helpers */
import { getClaimHash, getTransactions, HashClaim, Transaction, TransactionStatus } from '../api'
import { isValidEmail } from '../lib/helpers'

/* Components*/
import ClaimForm from './components/ClaimForm'
import QRHashForm from './components/QRHashForm'
import ClaimHeader from './components/ClaimHeader'
import ClaimLoading from './components/ClaimLoading'
import ClaimPending from './components/ClaimPending'
import ClaimFinished from './components/ClaimFinished'
import ClaimAlreadyMinted from './components/ClaimAlreadyMinted'
import { ClaimFooter } from 'components/ClaimFooter'

/* Assets */
import EmptyBadge from '../images/empty-badge.svg'
import { Helmet } from 'react-helmet'
import { FetchConnectionError } from '../errors/FetchErrorConnection'

export const CodeClaimPage: React.FC<RouteComponentProps<{ hash: string }>> = ({ match }) => {
  // State management
  const [claim, setClaim] = useState<null | HashClaim>(null)
  const [claimError, setClaimError] = useState<string | null>(null)
  const [isClaimLoading, setIsClaimLoading] = useState<boolean>(false)
  const [isVerified, setIsVerified] = useState<boolean>(false)
  const [isAlreadyMinted, setIsAlreadyMinted] = useState<boolean>(false)

  // Destructure url parameters
  const location = useLocation()
  const queryParams = queryString.parse(location.search)
  let { hash } = match.params

  // Component constants
  const address = queryParams.address ? queryParams.address.toString() : ''
  let title = 'Mint your POAP'
  let image = EmptyBadge
  let originalImage = image

  // If a hash was found in the url, trigger a claim for that hash
  useEffect(() => {
    if (hash) fetchClaim(hash)
  }, []) /* eslint-disable-line react-hooks/exhaustive-deps */

  // If claim metadata was received, verify that the user got the token they were trying to claim
  useEffect(() => {
    if (claim && !isVerified) {
      setIsVerified(true)
      if (claim?.tx?.status === TransactionStatus.PASSED) {
        setIsAlreadyMinted(true)
      }
    }
  }, [claim, isVerified]) /* eslint-disable-line react-hooks/exhaustive-deps */

  const fetchClaim = async (hash: string): Promise<null | HashClaim> => {
    setIsClaimLoading(true)
    try {
      const _claim = await getClaimHash(hash.toLowerCase())
      setClaim(_claim)
      setClaimError(null)
      setIsClaimLoading(false)
      return _claim
    } catch (error: unknown) {
      if (error instanceof FetchConnectionError) {
        setClaimError('There was a network error, please check your internet connection and try again.')
      } else if (
        error instanceof Error &&
        error.message.startsWith('You have been banned for reiterative incorrect usage of the API.')
      ) {
        let ipMessage = ''
        try {
          // Message: You have been banned for reiterative incorrect usage of the API. (IP: <ip>)
          ipMessage = error.message.split('(')[1]
          console.error(error.message)
          console.error(ipMessage)
        } catch (e) {
          console.error(e)
        }

        setClaimError(
          'Your IP has been blocked because of too many failed attempts. ' +
            'Please contact support if you think this is a mistake. (' +
            ipMessage,
        )
      } else {
        setClaimError("We couldn't find a POAP associated with this mint link.")
      }
      setIsClaimLoading(false)
    }
    return null
  }

  const continueClaim = (claim: HashClaim) => {
    setIsVerified(false)
    setClaim(claim)
  }

  const findTransaction = async (qr_hash: string): Promise<Transaction | undefined> => {
    const txs = await getTransactions({ qr_hash })

    //todo: this line should change when we have a criteria to select the most recent tx to check status
    const tx = txs.total > 0 ? txs.transactions[0] : undefined

    if (tx && claim) {
      claim.tx = tx
      claim.tx_status = tx.status
      if (tx.tx_hash) {
        claim.tx_hash = tx.tx_hash
      }

      setClaim({ ...claim })
    }

    return tx
  }

  /* Render component */
  const claimedWithEmail = !!(
    claim &&
    claim.claimed &&
    claim.user_input &&
    isValidEmail(claim.user_input) &&
    !claim.event.minting_config?.available_wallet_providers?.length
  )

  const isLoading = claim && !claimError && !isVerified
  let body = (
    <QRHashForm loading={isClaimLoading} checkClaim={fetchClaim} errorMessage={claimError} qrHash={hash || ''} />
  )

  if (claim?.event.image_url) {
    image = claim.event.image_url
    originalImage = claim.event.original_image_url || image
  }

  if (claim && !isLoading && !isAlreadyMinted) {
    body = <ClaimForm claim={claim} address={address} onSubmit={continueClaim} />
    title = claim.event.name
    if (claim.claimed) {
      // POAP minting
      body = <ClaimPending claim={claim} findTransaction={findTransaction} />

      if ((claim.tx_status && claim.tx_status === TransactionStatus.PASSED) || claimedWithEmail) {
        body = <ClaimFinished claim={claim} />
      }
    }
  }

  if (claim && isAlreadyMinted) {
    title = claim.event.name
    body = <ClaimAlreadyMinted claim={claim} />
  }

  if (isLoading) {
    body = <ClaimLoading />
  }

  return (
    <>
      <Helmet>
        <title>POAP - Claim</title>
        <meta property="og:title" content="POAP - Claim" />
      </Helmet>
      {hash && !claim && !claimError ? (
        <ClaimLoading />
      ) : (
        <div className={'code-claim-page claim'}>
          <ClaimHeader
            title={title}
            image={image}
            originalImage={originalImage}
            claimed={!!(claim && claim.tx_status === TransactionStatus.PASSED) || claimedWithEmail}
          />

          <div className={`claim-body`}>{body}</div>
          <ClaimFooter />
        </div>
      )}
    </>
  )
}
